<template>
  <div v-if="!isBigger" class="speech-control mr-2 position-relative tooltip-outer">
    <button type="button" class="speech-button" :class="{ onreconation_icon: processing }"
      :disabled="processing || isDisabledInput || remainPoints < 1" :aria-label="buttonLabel" :title="buttonLabel" @click="toggleRecording">
      <canvas v-if="state === 'recording'" ref="record" width="54" height="54" aria-hidden="true" />
      <img v-else-if="state === 'preparing' || state === 'connecting' || processing" src="@/assets/image/ai/mic_C_onreconation.png" alt="">
      <img v-else src="@/assets/image/ai/mic_C.png" alt="">
    </button>
    <button v-if="processing" type="button" class="cancel-recording" @click="cancelRecording">{{ $t('ersRecording.cancel') }}</button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ChatSpeechSession } from '@/utils/chatSpeechSession'
import { fetchErsStreamToken } from '@/utils/ersStreamToken'
import { ersSpeechLanguage } from '@/utils/ersSpeechLanguage'
import { recordingMessages } from '@/locales/ersRecording'

export default {
  name: 'SpeechRecognition',
  i18n: { messages: recordingMessages },
  props: {
    isBigger: { type: Boolean, default: false },
    isDisabledInput: { type: Boolean, default: false }
  },
  data () {
    return { state: 'idle', transcript: '', assessment: null, statusMessage: '', statusError: false }
  },
  computed: {
    ...mapGetters(['remainPoints', 'selectedAccent']),
    recordingLanguage () { return ersSpeechLanguage(this.selectedAccent) },
    processing () { return this.state === 'finishing' },
    busy () { return ['preparing', 'connecting', 'recording', 'finishing'].includes(this.state) },
    buttonLabel () {
      if (this.remainPoints < 1) return this.$t('ersRecording.unavailable')
      const key = { recording: 'stop', preparing: 'cancelPreparing', connecting: 'cancelConnecting', finishing: 'finishing' }[this.state] || 'start'
      return this.$t('ersRecording.' + key)
    }
  },
  watch: {
    recordingLanguage () { this.cancelRecording() },
    isDisabledInput (value) { if (value) this.cancelRecording() },
    isBigger (value) { if (value) this.cancelRecording() },
    remainPoints (value) { if (value < 1) this.cancelRecording() }
  },
  created () {
    // Keep browser resource objects out of Vue 2's reactive observation.
    this._ersSession = null
    this._ersFrame = null
  },
  beforeDestroy () { this.cancelRecording() },
  methods: {
    stopDrawing () { cancelAnimationFrame(this._ersFrame); this._ersFrame = null },
    draw (current) {
      if (this._ersSession !== current || this.state !== 'recording') return
      const canvas = this.$refs.record
      const ctx = canvas && canvas.getContext('2d')
      if (ctx) {
        const samples = current.getWaveform()
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 1.5
        ctx.beginPath()
        samples.forEach((sample, index) => {
          const x = index * canvas.width / samples.length
          const y = sample * canvas.height / 256
          if (index) ctx.lineTo(x, y)
          else ctx.moveTo(x, y)
        })
        ctx.stroke()
      }
      this._ersFrame = requestAnimationFrame(() => this.draw(current))
    },
    reportState (message = this.statusMessage, error = this.statusError) {
      this.statusMessage = message
      this.statusError = error
      this.$emit('speechStatus', { busy: this.busy, message, error, transcript: this.transcript, assessment: this.assessment })
    },
    startRecording () {
      if (this.isDisabledInput || this.remainPoints < 1 || this.busy) return
      this.transcript = ''
      this.assessment = null
      const lang = this.recordingLanguage
      const current = new ChatSpeechSession({
        lang,
        getToken: fetchErsStreamToken,
        onState: value => {
          if (this._ersSession !== current) return
          this.state = value
          if (value !== 'recording') this.stopDrawing()
          this.$emit('speechCountDown', { isSpeechRecognition: value === 'recording', count: 16 })
          const message = ['preparing', 'connecting', 'recording', 'finishing', 'complete'].includes(value) ? this.$t('ersRecording.' + value) : ''
          this.reportState(message, false)
          if (value === 'recording') this.$nextTick(() => { if (this._ersSession === current) this.draw(current) })
        },
        onCountdown: count => {
          if (this._ersSession === current) this.$emit('speechCountDown', { isSpeechRecognition: true, count })
        },
        onTranscript: value => {
          if (this._ersSession !== current) return
          this.transcript = [value.confirmed, value.interim].filter(Boolean).join(lang === 'ja-JP' ? '' : ' ')
          this.reportState()
        },
        onResult: result => {
          if (this._ersSession !== current) return
          this.transcript = ''
          this.assessment = result.assessment
          this.$emit('emitSpeechRecognition', result)
          this.reportState()
          this.$store.dispatch('getRemainPoints')
        },
        onError: error => {
          if (this._ersSession !== current) return
          this.transcript = ''
          this.assessment = null
          const errors = recordingMessages.tw.ersRecording.errors
          const key = errors[error.code] ? error.code : errors[error.name] ? error.name : 'DEFAULT'
          this.reportState(this.$t('ersRecording.errors.' + key), true)
        }
      })
      this._ersSession = current
      current.start()
    },
    cancelRecording () {
      const current = this._ersSession
      this._ersSession = null
      if (current) current.cancel()
      this.stopDrawing()
      this.state = 'idle'
      this.transcript = ''
      this.assessment = null
      this.$emit('speechCountDown', { isSpeechRecognition: false, count: 16 })
      this.reportState('', false)
    },
    toggleRecording () {
      if (this.state === 'recording') this._ersSession.stop()
      else if (['preparing', 'connecting'].includes(this.state)) this.cancelRecording()
      else this.startRecording()
    }
  }
}
</script>

<style lang="scss" scoped>
.speech-control { flex-shrink: 0; }
.speech-button { display: block; position: relative; padding: 0; border: 0; background: transparent; cursor: pointer; width: 3rem; height: 3rem; }
.speech-button:disabled { opacity: .55; cursor: default; }
.speech-button:focus-visible, .cancel-recording:focus-visible { outline: 3px solid #6a69ad; outline-offset: 3px; }
.speech-button img, canvas { width: 100%; height: 100%; object-fit: contain; }
canvas { background: url('~@/assets/image/ai/mic_C_onrecord.png') center / contain no-repeat; }
.cancel-recording { display: block; margin: 2px auto 0; padding: 0; border: 0; background: transparent; color: #5a5d9b; font-size: 12px; text-decoration: underline; cursor: pointer; }
.onreconation_icon::after { content: ''; position: absolute; inset: 15%; background: url('~@/assets/image/ai/mic_loading.png') center / contain no-repeat; animation: spin 2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 767px) { .speech-button { width: 40px; height: 40px; } }
@media (prefers-reduced-motion: reduce) { .onreconation_icon::after { animation: none; } }
</style>
